/** GeoJSON-shaped location as returned by GET /api/v1/search. */
export interface SearchResultLocation {
  type: string
  coordinates: number[] | number[][]
}

export interface SearchResultLike {
  type: string
  location: SearchResultLocation | null
}

/** A map movement request: point fly-to or bounds fit. */
export type SearchZoomTarget =
  | { kind: 'point'; lat: number; lng: number; zoom: number }
  | { kind: 'bounds'; bounds: [[number, number], [number, number]] }

const POINT_ZOOM: Record<string, number> = {
  meter: 18,
  transformer: 17,
  device: 17,
  substation: 16,
  feeder: 15, // fallback only — feeders normally carry a LineString route
}

/**
 * Compute the map movement for a picked search result: type-appropriate point
 * zoom for point features, fitBounds for line geometries (feeder routes),
 * null when the result has no usable geometry ("No map location on record").
 */
export function zoomForSearchResult(result: SearchResultLike): SearchZoomTarget | null {
  const loc = result.location
  if (!loc || !Array.isArray(loc.coordinates) || loc.coordinates.length === 0) {
    return null
  }
  if (loc.type === 'Point') {
    const [lng, lat] = loc.coordinates as number[]
    if (typeof lat !== 'number' || typeof lng !== 'number') return null
    return { kind: 'point', lat, lng, zoom: POINT_ZOOM[result.type] ?? 17 }
  }
  if (loc.type === 'LineString') {
    const coords = loc.coordinates as number[][]
    const lats = coords.map((c) => c[1])
    const lngs = coords.map((c) => c[0])
    return {
      kind: 'bounds',
      bounds: [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)],
      ],
    }
  }
  return null
}
